import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { includes } from 'lodash';
import isEqual from 'lodash/isEqual';
import { useContext, useEffect, useMemo, useState } from 'react';
import { ButtonGroup, Col, Container, Row, Spinner, ToggleButton } from 'react-bootstrap';
import { BsGrid3X3GapFill, BsInfoCircle, BsJustify } from 'react-icons/bs';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  BookingRequestRead,
  BookingRequestReadStatusEnum,
  BookingRequestsService,
  UserTypeEnum,
} from '../api';
import { BookingRequestCard } from '../app/components/BookingRequestCard/BookingRequestCard';
import { BookingRequestDialog } from '../app/components/BookingRequestDialog';
import {
  BookingRequestsFilters,
  BookingRequestsFiltersState,
  BookingRequestsListParameters,
  defaultFilter,
  SERVER_DATE_FORMAT,
} from '../app/components/BookingRequestsFilters';
import { Card } from '../app/components/Card';
import { useConfirmationModal } from '../app/components/ConfirmationModal';
import { ListFooter } from '../app/components/ListFooter';
import {
  PendingPlacementRequestsBadge,
  RespondedPlacementRequestsBadge,
} from '../app/components/NotificationBadge';
import { ProfileIncompleteCard } from '../app/components/ProfileIncompleteCard';
import { ShiftRequestsTable } from '../app/components/ShiftRequestsTable';
import { TabsNavbar } from '../app/components/TabsNavbar';
import { getAssignmentsPageSearchPlaceholder } from '../app/constants/healthcare-entity';
import { BUTTON_CLASS_NAME, BUTTON_STYLES } from '../app/constants/view-switcher';
import { SessionContext } from '../app/contexts/SessionContext';
import { useCurrentUser } from '../app/hooks/auth';
import { SIZE_LG, useBreakpoint } from '../app/hooks/breakpoint';
import { useSearchParamsState } from '../app/hooks/search-params';
import { useDocumentTitle } from '../app/hooks/use-document-title';
import { useUserProgress } from '../app/hooks/user-progress';
import { bookingRequestsQueryKeys } from '../app/query-keys-factories/booking-requests';
import { userQueryKeys } from '../app/query-keys-factories/user';
import { cleanAndSortOptions } from '../utils/forms';
import { useBookingRequestsFilters } from './hooks/useBookingRequestsFilters';

const BookingRequestsEmptyInfoMessage = () => (
  <Row className="m-1">
    <div className="empty-list-info-message">
      <p>
        <BsInfoCircle className="tw:inline me-2 mb-1" />
        Once you have an approved connection, you can manage your assignment requests here.
      </p>
      <p>
        This page includes all the details for each assignment, such as shift hours, payment terms,
        travel arrangements, and any bonuses.
      </p>
    </div>
  </Row>
);

/* eslint-disable sonarjs/cognitive-complexity*/
export const BookingRequestList = () => {
  useDocumentTitle('Assignments');
  const [isGridView, setIsGridView] = useState(true);
  const { user } = useCurrentUser();
  const userIsProvider = user?.user_type === UserTypeEnum.PROVIDER;
  const breakpoint = useBreakpoint();
  const showOnlyTails = useMemo(() => !includes(breakpoint, SIZE_LG), [breakpoint]);
  const [showFullDecline, setShowFullDecline] = useState(false);
  const isShowGrid = useMemo(() => isGridView && !showOnlyTails, [isGridView, showOnlyTails]);
  const { bookingId } = useParams();
  const { notifications } = useContext(SessionContext) as {
    notifications?: {
      isLoading: boolean;
      notifications?: Record<string, number>;
    };
  };

  const initialStatus = useMemo(() => {
    const pendingCount = notifications?.notifications?.['placement_requests'] ?? 0;
    const respondedCount = notifications?.notifications?.['placement_responded_requests'] ?? 0;

    if (userIsProvider) {
      if (pendingCount > 0) return BookingRequestReadStatusEnum.PENDING;
      if (respondedCount > 0) return BookingRequestReadStatusEnum.RESPONDED;
    } else {
      if (respondedCount > 0) return BookingRequestReadStatusEnum.RESPONDED;
      if (pendingCount > 0) return BookingRequestReadStatusEnum.PENDING;
    }

    return BookingRequestReadStatusEnum.CONFIRMED;
  }, [notifications?.notifications, userIsProvider]);

  const [statusIn, setStatusIn] = useSearchParamsState<BookingRequestReadStatusEnum[]>(
    'status__in',
    [initialStatus],
    true
  );

  const statusInSerialized = useMemo(() => JSON.stringify(statusIn), [statusIn]);

  const { filters, setFilters } = useBookingRequestsFilters();
  const [date, setDate] = useSearchParamsState<BookingRequestsListParameters['date']>(
    'date',
    'UPCOMING'
  );
  const [page, setPage] = useSearchParamsState('page', '1');
  const LIMIT = 12;
  const offset = (parseInt(page) - 1) * LIMIT;
  const { progress, isProfileComplete, lastStep } = useUserProgress(
    user?.provider_completed_registration_steps
  );
  const { ConfirmationModal, showConfirmationModal } = useConfirmationModal();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const queryClient = useQueryClient();
  const queryParams = useMemo(
    () => ({
      limit: LIMIT,
      offset,
      date,
      dateFrom: filters.shiftDate[0],
      dateTo: filters.shiftDate[1],
      statusIn: statusIn,
      ...filters,
    }),
    [LIMIT, offset, date, filters, statusIn]
  );

  const {
    data: bookingRequestsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: bookingRequestsQueryKeys.list(queryParams),
    queryFn: async () => {
      return await BookingRequestsService.bookingRequestsList(preparePayload(queryParams));
    },
    onSuccess: () => {
      queryClient.invalidateQueries(userQueryKeys.notifications());
    },
  });

  const [facilitiesOptions, setFacilitiesOptions] = useState<Record<string, any>[]>([]);

  const preparePayload = (queryParams) => {
    const isHCO = user?.user_type !== UserTypeEnum.PROVIDER;
    if (isHCO) {
      queryParams.searchByProvider = queryParams.search;
      delete queryParams.search;
    }

    return {
      ...queryParams,
      dateFrom: queryParams.dateFrom
        ? format(new Date(queryParams.dateFrom), SERVER_DATE_FORMAT)
        : '',
      dateTo: queryParams.dateTo ? format(new Date(queryParams.dateTo), SERVER_DATE_FORMAT) : '',
    };
  };

  useEffect(() => {
    setFacilitiesOptions([]);
  }, [statusInSerialized, date]);

  useEffect(() => {
    if (bookingRequestsData?.results && facilitiesOptions.length === 0) {
      const options = bookingRequestsData.results.map((br) => ({
        id: br.healthcare_facility.id,
        name: br.healthcare_facility.name,
      }));
      setFacilitiesOptions(cleanAndSortOptions(options, 'id', 'name'));
    }
  }, [bookingRequestsData?.results, facilitiesOptions.length]);

  useEffect(() => {
    // Only run it once to make the pending page the default one
    // when getting here directly from the link
    if (!date) {
      setDate('UPCOMING');
      setFilters(defaultFilter);
    }
  }, []);

  const bookingRequest = useMemo(() => {
    return bookingRequestsData?.results?.find((br) => br.id === bookingId);
  }, [bookingId, bookingRequestsData?.results]);

  const onBookingRequestClick = (bookingRequestId: string) => {
    navigate({ pathname: `${bookingRequestId}`, search: searchParams.toString() });
  };

  const onHideHandler = () => {
    navigate(
      { pathname: '..', search: searchParams.toString() },
      { relative: 'path', replace: true }
    );
  };

  const searchPlaceholder = useMemo(() => {
    return getAssignmentsPageSearchPlaceholder(userIsProvider);
  }, [userIsProvider]);

  const searchOptions = useMemo(() => {
    return facilitiesOptions?.map((facility) => facility.name);
  }, [facilitiesOptions]);

  const onUpdateSort = useMemo(
    () => (data) => {
      setFilters({
        ...filters,
        sortBy: data.key,
        sortDirection: data.direction,
      } as unknown as BookingRequestsFiltersState);
    },
    [filters, setFilters]
  );

  return (
    <div>
      <div className="bg-primary-dark">
        <Container className="page-header-title">
          <div className="d-flex flex-wrap">
            <h1 className="flex-grow-1">Assignments</h1>
          </div>
        </Container>
      </div>
      <div className="bg-primary-dark">
        <Container>
          <TabsNavbar
            variant="primary-dark"
            tabs={[
              {
                itemID: 'upcoming',
                onClick: () => {
                  setPage('1');
                  setStatusIn([BookingRequestReadStatusEnum.CONFIRMED]);
                  setDate('UPCOMING');
                  setFilters({
                    ...defaultFilter,
                    sortDirection: 'asc',
                    sortBy: 'first_shift',
                  });
                },
                label: 'Upcoming',
                active:
                  isEqual(statusIn, [BookingRequestReadStatusEnum.CONFIRMED]) &&
                  date === 'UPCOMING',
              },
              {
                itemID: 'pending',
                onClick: () => {
                  setPage('1');
                  setStatusIn([BookingRequestReadStatusEnum.PENDING]);
                  setDate('UPCOMING');
                  setFilters({
                    ...defaultFilter,
                    sortDirection: 'asc',
                    sortBy: 'first_shift',
                  });
                },
                label: (
                  <span>
                    {userIsProvider ? `Needs Response` : `Waiting on Provider`}{' '}
                    <PendingPlacementRequestsBadge />
                  </span>
                ),
                active:
                  isEqual(statusIn, [BookingRequestReadStatusEnum.PENDING]) && date === 'UPCOMING',
              },
              {
                itemID: 'waiting-confirmation',
                onClick: () => {
                  setPage('1');
                  setStatusIn([BookingRequestReadStatusEnum.RESPONDED]);
                  setDate('UPCOMING');
                  setFilters({
                    ...defaultFilter,
                    sortDirection: 'asc',
                    sortBy: 'first_shift',
                  });
                },
                label: (
                  <span>
                    {userIsProvider ? `Waiting on Confirmation` : `Needs Confirmation`}
                    <RespondedPlacementRequestsBadge />
                  </span>
                ),
                active:
                  isEqual(statusIn, [BookingRequestReadStatusEnum.RESPONDED]) &&
                  date === 'UPCOMING',
              },
              {
                itemID: 'completed',
                onClick: () => {
                  setPage('1');
                  setStatusIn([BookingRequestReadStatusEnum.CONFIRMED]);
                  setDate('PAST');
                  setFilters({
                    ...defaultFilter,
                    sortDirection: 'desc',
                    sortBy: 'first_shift',
                  });
                },
                label: 'Recently Completed',
                active: date === 'PAST',
              },
              {
                itemID: 'archived',
                onClick: () => {
                  setPage('1');
                  setStatusIn([BookingRequestReadStatusEnum.CONFIRMED]);
                  setDate('ARCHIVE');
                  setFilters({
                    ...defaultFilter,
                    sortDirection: 'desc',
                    sortBy: 'first_shift',
                  });
                },
                label: 'Archived',
                active: date === 'ARCHIVE',
              },
              {
                itemID: 'canceled',
                onClick: () => {
                  setPage('1');
                  setStatusIn([BookingRequestReadStatusEnum.CANCELED]);
                  setDate('ALL');
                  setFilters({
                    ...defaultFilter,
                    sortDirection: 'desc',
                    sortBy: 'updated_at',
                  });
                },
                label: 'Canceled',
                active: isEqual(statusIn, [BookingRequestReadStatusEnum.CANCELED]),
              },
            ]}
          />
        </Container>
      </div>
      <Container className="mt-3">
        <Card className="mb-3">
          <Row className="p-3">
            <Col lg="9" xl="10" xs="12">
              <BookingRequestsFilters
                searchPlaceholder={searchPlaceholder}
                filters={filters}
                searchOptions={isLoading ? [] : (searchOptions as [])}
                facilitiesOptions={isLoading ? [] : (facilitiesOptions as [])}
                onApply={(data) => {
                  setPage('1');
                  setFilters(data);
                }}
                statusIn={statusIn}
                userIsProvider={userIsProvider}
              />
            </Col>
            {!showOnlyTails && (
              <Col xs="auto" className="ms-auto align-self-center">
                <ButtonGroup className="primary-lite-bg-toggle">
                  <ToggleButton
                    className={BUTTON_CLASS_NAME}
                    style={BUTTON_STYLES}
                    type="radio"
                    value="1"
                    name="view_type_grid"
                    variant="outline-primary"
                    checked={isShowGrid}
                    onClick={() => setIsGridView(true)}
                    onKeyUp={(e) => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        setIsGridView(true);
                      }
                    }}
                    size="sm"
                  >
                    <BsJustify size={17} />
                  </ToggleButton>
                  <ToggleButton
                    className={BUTTON_CLASS_NAME}
                    style={BUTTON_STYLES}
                    type="radio"
                    value="0"
                    name="view_type_tails"
                    variant="outline-primary"
                    checked={!isShowGrid}
                    onClick={() => setIsGridView(false)}
                    onKeyUp={(e) => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        setIsGridView(false);
                      }
                    }}
                    size="sm"
                  >
                    <BsGrid3X3GapFill size={17} />
                  </ToggleButton>
                </ButtonGroup>
              </Col>
            )}
          </Row>
        </Card>
        {!isShowGrid && isLoading && (
          <Row>
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <Spinner
                aria-label="loading indicator"
                animation="grow"
                className="loading-spinner"
                variant="primary"
              />
            </Col>
          </Row>
        )}
        {!isShowGrid &&
        !isLoading &&
        bookingRequestsData?.results &&
        bookingRequestsData.results.length > 0 ? (
          <Row>
            {bookingRequestsData.results.map((bookingRequest) => (
              <Col xs={12} md={6} lg={4} className="mb-4" key={bookingRequest.id}>
                <BookingRequestCard
                  bookingRequest={bookingRequest}
                  onBookingRequestClick={onBookingRequestClick}
                />
              </Col>
            ))}
          </Row>
        ) : (
          !isShowGrid &&
          (userIsProvider ? (
            <BookingRequestsEmptyInfoMessage />
          ) : (
            <Row className="ms-2 text-muted">No Assignments Found</Row>
          ))
        )}
        {isShowGrid && (isProfileComplete || !userIsProvider) && (
          <ShiftRequestsTable
            filters={filters}
            onUpdateSort={onUpdateSort}
            onRowClick={(data: BookingRequestRead) => onBookingRequestClick(data.id)}
            statusFilter={statusIn}
            isLoading={isLoading}
            list={bookingRequestsData?.results || []}
            pageSize={LIMIT}
            totalCount={bookingRequestsData?.count || 0}
            currentPage={parseInt(page)}
            handleChangePage={(page: number) => setPage(page.toString())}
            isProvider={userIsProvider}
          />
        )}
        {isShowGrid &&
          userIsProvider &&
          isProfileComplete &&
          !!bookingRequestsData?.results &&
          bookingRequestsData?.results?.length === 0 && <BookingRequestsEmptyInfoMessage />}
        {!isLoading && !isProfileComplete && userIsProvider && (
          <Row className="align-items-center justify-content-center">
            <Col lg="4">
              <ProfileIncompleteCard progress={progress} />
            </Col>
          </Row>
        )}

        {!isShowGrid && (
          <ListFooter
            title="Assignment"
            totalCount={bookingRequestsData?.count || 0}
            pageSize={LIMIT}
            currentPage={parseInt(page)}
            onChangePage={(page: number) => setPage(page.toString())}
            containerClassName="py-2 px-2 text-muted"
          />
        )}
      </Container>
      {!!bookingRequest && (
        <BookingRequestDialog
          bookingRequest={bookingRequest}
          show={!!bookingRequest}
          showConfirmationModal={showConfirmationModal}
          onHide={() => onHideHandler()}
          refetch={refetch}
          setShowFullDecline={setShowFullDecline}
          showFullDecline={showFullDecline}
        />
      )}
      <ConfirmationModal
        key={showFullDecline ? 'decline' : 'accept'}
        title="Assignment Agreement"
        htmlText={
          showFullDecline ? (
            <>
              <h5 className="text-section-name--dark-brand mb-2">
                {bookingRequest?.healthcare_facility.name}
              </h5>
              <div className="">
                Are you sure you want to decline <span className="fw-bold text-danger">ALL</span>{' '}
                shifts?
              </div>
              <div className="text-medium text-muted bg-neutral-200 p-2 rounded mt-3">
                If you continue the assignment will be CANCELED. <br /> <br />
                To accept any shift, go back and place check mark next to it and then click the
                Respond to Assignment button.
              </div>
            </>
          ) : (
            <>
              <h5 className="text-section-name--dark-brand mb-2">
                {bookingRequest?.healthcare_facility.name}
              </h5>
              <div className="">
                By accepting, you <b>acknowledge and agree to</b>
                <ul>
                  <li>dates and times for any shift selected with a checkmark</li>
                  <li>fixed hourly rates noted next to a selected shift</li>
                  <li>
                    Any <b>unchecked</b> shifts will be{' '}
                    <span className="text-danger">rejected</span>
                  </li>
                </ul>
              </div>
              <div className="text-medium text-muted bg-neutral-200 p-2 rounded">
                You also agree to any facility requirements including but not limited to APP
                supervision, chart completion and compliance regulations as outlined in the Provider
                Agreement.
              </div>
            </>
          )
        }
        confirmText={showFullDecline ? 'Decline' : 'Accept'}
        confirmVariant={showFullDecline ? 'danger' : 'primary'}
        cancelText="Go Back"
        cancelVariant="outline-primary"
      />
    </div>
  );
};
